import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Paper, Typography, Button, Box, Avatar, Chip, Alert, CircularProgress, Slider,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { providerApi } from '../../services/api';
import { format, isValid, parseISO } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';

// ── Section shell (matches the dashboard's border-based card style) ──────────
function Section({ title, action, children, sx }) {
  return (
    <Paper
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: { xs: 2, sm: 3 }, mb: 2.5, ...sx }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, minHeight: 32 }}>
        <Typography
          variant="caption"
          sx={{ color: 'text.disabled', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.68rem' }}
        >
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </Paper>
  );
}

const AvailabilitySettings = ({ providerData }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const { refreshProviderData } = useAuth();
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [availabilityRadius, setAvailabilityRadius] = useState(providerData.availabilityRadius || 5);

  // Helper to ensure we work with yyyy-MM-dd strings
  const toDateStr = (dateInput) => {
    if (!dateInput) return null;
    const d = typeof dateInput === 'string' ? parseISO(dateInput) : new Date(dateInput);
    return isValid(d) ? format(d, 'yyyy-MM-dd') : null;
  };

  const [availabilityDates, setAvailabilityDates] = useState(
      providerData.availabilityDates?.map(d => toDateStr(d)).filter(Boolean) || []
  );

  const handleEdit = () => {
    setIsEditing(true);
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setAvailabilityRadius(providerData.availabilityRadius || 5);
    setAvailabilityDates(providerData.availabilityDates?.map(d => toDateStr(d)).filter(Boolean) || []);
    setError('');
    setSuccess('');
  };

  const handleDateClick = (date) => {
    if (!isEditing || !date) return;
    const dateStr = toDateStr(date);
    if (!dateStr) return;

    setAvailabilityDates(prev =>
        prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr]
    );
  };

  const renderDay = (props) => {
    const { day } = props;

    // Safety check for date-fns v3/v4
    if (!day || !isValid(new Date(day))) {
      return <PickersDay {...props} />;
    }

    const dateStr = format(new Date(day), 'yyyy-MM-dd');
    const isAvailable = availabilityDates.includes(dateStr);

    return (
        <PickersDay
            {...props}
            sx={{
              ...(isAvailable && {
                backgroundColor: `${primary} !important`,
                color: '#fff !important',
                fontWeight: 700,
                '&:hover': { backgroundColor: `${theme.palette.primary.dark} !important` },
              }),
            }}
        />
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    try {
      // Dedicated availability endpoint (design §4.7): full replace of the two
      // availability fields only — profile fields can no longer be clobbered from here.
      await providerApi.updateAvailability({ availabilityDates, availabilityRadius });
      // Refresh shared data in place (no full reload, keeps current tab)
      await refreshProviderData();
      setSuccess(t('availability.updated'));
      setIsEditing(false);
      setIsSaving(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.code === 'PRO_REQUIRED' ? t('gate.upgradeCta') : (err.response?.data?.message || t('availability.updateFailed')));
      setIsSaving(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{t('availability.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('availability.subtitle')}
          </Typography>
        </Box>
        {!isEditing && (
          <Button variant="contained" startIcon={<EditIcon />} onClick={handleEdit}>
            {t('availability.editButton')}
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2.5 }}>{success}</Alert>}

      {/* Service radius */}
      <Section title={t('availability.radiusTitle')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Avatar sx={{ bgcolor: alpha(primary, 0.1), color: primary, width: 42, height: 42, borderRadius: 1.5 }}>
            <MyLocationIcon sx={{ fontSize: 22 }} />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>
              {t('availability.radiusKm', { km: availabilityRadius })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('availability.radiusCaption')}
            </Typography>
          </Box>
        </Box>
        <Slider
          value={availabilityRadius}
          onChange={(e, val) => setAvailabilityRadius(val)}
          disabled={!isEditing}
          min={0}
          max={100}
          valueLabelDisplay="auto"
          sx={{ mt: 1 }}
        />
      </Section>

      {/* Available dates */}
      <Section
        title={t('availability.datesTitle')}
        action={
          <Chip
            size="small"
            icon={<EventAvailableIcon sx={{ fontSize: 15 }} />}
            label={t('availability.selectedCount', { count: availabilityDates.length })}
            sx={{ bgcolor: alpha(primary, 0.1), color: primary, fontWeight: 600 }}
          />
        }
      >
        {isEditing && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('availability.tapHint')}
          </Typography>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: alpha(primary, 0.02),
            py: 1,
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateCalendar
              onChange={handleDateClick}
              readOnly={!isEditing}
              slots={{ day: renderDay }}
            />
          </LocalizationProvider>
        </Box>
      </Section>

      {/* Save / cancel */}
      {isEditing && (
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancel} disabled={isSaving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={isSaving}
          >
            {t('availability.saveButton')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default AvailabilitySettings;
